// Pure scheduling rules shared by the server and terminal UI; no runtime dependencies.
type ScheduledRun = { status: string; disposition?: string; closeNote?: string | null; plannedStartAt?: string | null; plannedEndAt?: string | null };
const shiftLength = 8 * 3_600_000;
const turkeyOffset = 3 * 3_600_000;
export const UNSCHEDULED_PLAN_SHIFT_CODE = "UNSCHEDULED";

export function planWorkOrderIssue(run: { planFlowKey?: string | null; planWorkOrderMatch?: string | null; workOrderNo: string }) {
  if (!run.planFlowKey) return null;
  if (run.planWorkOrderMatch === "AMBIGUOUS") return "Mikro iş emri eşleşmesi belirsiz. Planlama sorumlusu doğru iş emrini doğrulamalı.";
  if (run.planWorkOrderMatch === "MISSING" || run.workOrderNo.startsWith("PLAN-")) return "Mikro iş emri eşleşmesi eksik. Planlama sorumlusu eşleşmeyi tamamlamalı.";
  return null;
}

type PlanStep = { planWeek?: string | null; lineId?: string; planSourceRow?: number | null; sequenceNo?: number; plannedStartAt?: string | null };

export function comparePlanSteps(a: PlanStep, b: PlanStep) {
  if (a.planWeek && a.planWeek === b.planWeek && a.lineId === b.lineId && a.planSourceRow != null && b.planSourceRow != null) {
    return a.planSourceRow - b.planSourceRow;
  }
  return (a.plannedStartAt ?? "").localeCompare(b.plannedStartAt ?? "") || (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0);
}

export function needsCarryoverDecision(run: ScheduledRun, now = Date.now()) {
  if (run.status !== "READY" || run.disposition === "CANCELLED") return false;
  if (run.disposition === "ON_HOLD" && run.closeNote !== "PLAN_WINDOW_EXPIRED") return false;
  const end = Date.parse(run.plannedEndAt ?? "");
  return Number.isFinite(end) && now >= end;
}

export function plannedShiftLabel(run: ScheduledRun) {
  const start = Date.parse(run.plannedStartAt ?? ""), end = Date.parse(run.plannedEndAt ?? "");
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return "Plan saati belirtilmedi";
  const first = Math.floor((start + turkeyOffset) / shiftLength);
  const last = Math.floor((end - 1 + turkeyOffset) / shiftLength);
  const label = (slot: number) => `${((slot % 3) + 3) % 3 + 1}. Vardiya`;
  return first === last ? label(first) : `${label(first)} → ${label(last)}`;
}

export function operationalShiftAt(now = Date.now(), offset = 0) {
  const slot = Math.floor((now + turkeyOffset) / shiftLength) + offset;
  const start = slot * shiftLength - turkeyOffset;
  const date = new Date(start + turkeyOffset).toISOString().slice(0,10);
  return { date, shiftCode: `${((slot % 3) + 3) % 3 + 1}. Vardiya`, start, end: start + shiftLength };
}

export function shiftInterval(date: string, shiftCode: string) {
  if (shiftCode === UNSCHEDULED_PLAN_SHIFT_CODE) {
    throw new RangeError("Saati belirlenmemiş plan satırının vardiya aralığı yoktur.");
  }
  const match = /([123])/.exec(shiftCode);
  const shift = match ? Number(match[1]) : 1;
  const start = Date.parse(`${date}T00:00:00+03:00`) + (shift - 1) * shiftLength;
  return { start, end: start + shiftLength, date, shiftCode: `${shift}. Vardiya` };
}

export function calendarWeekForDate(date: string) {
  const anchor = Date.parse(`${date}T00:00:00Z`);
  if (!Number.isFinite(anchor)) return null;
  const mondayOffset = (new Date(anchor).getUTCDay() + 6) % 7;
  const weekStart = new Date(anchor - mondayOffset * 86_400_000).toISOString().slice(0, 10);
  const weekEnd = new Date(anchor + (6 - mondayOffset) * 86_400_000).toISOString().slice(0, 10);
  return { weekStart, weekEnd };
}

export function planIntersects(startAt: string | null | undefined, endAt: string | null | undefined, start: number, end: number) {
  const planStart = Date.parse(startAt ?? "");
  const planEnd = Date.parse(endAt ?? "");
  return Number.isFinite(planStart) && Number.isFinite(planEnd) && planStart < end && planEnd > start;
}

export function comparePlannedRuns(a: { plannedStartAt?: string | null; lineName?: string; sequenceNo?: number; planSourceRow?: number | null; id?: string }, b: { plannedStartAt?: string | null; lineName?: string; sequenceNo?: number; planSourceRow?: number | null; id?: string }) {
  return (a.plannedStartAt ?? "").localeCompare(b.plannedStartAt ?? "") ||
    (a.lineName ?? "").localeCompare(b.lineName ?? "", "tr-TR", { numeric: true }) ||
    (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0) ||
    (a.planSourceRow ?? 0) - (b.planSourceRow ?? 0) ||
    (a.id ?? "").localeCompare(b.id ?? "");
}

type WeeklyPlanRun = Parameters<typeof comparePlannedRuns>[0] & {
  planWeek?: string | null;
  lineId: string;
  shiftDate: string;
  plannedEndAt?: string | null;
  planItemKind?: "PRODUCTION" | "ACTIVITY" | null;
  disposition?: string;
};

export function runsForTerminalWeek<T extends WeeklyPlanRun & { status: string }>(runs: T[], lineId: string | null, weekStart: string | null, weekEnd: string | null) {
  const weekly = runsForPlanWeek(runs, lineId, weekStart, weekEnd);
  const carryover = runs.filter((run) => run.lineId === lineId && run.status === "ACTIVE" &&
    run.planItemKind !== "ACTIVITY" && run.disposition !== "CANCELLED" &&
    !weekly.some((item) => item.id === run.id));
  // Active carryover stays reachable; this week's Excel rows retain source order
  // even after a supervisor changes a planned timestamp.
  const sourceOrdered = weekly.filter(run => run.planWeek === weekStart && run.planSourceRow != null).sort((a, b) => (a.planSourceRow ?? 0) - (b.planSourceRow ?? 0));
  const other = weekly.filter(run => !sourceOrdered.includes(run)).sort(comparePlannedRuns);
  return [...carryover.sort(comparePlannedRuns), ...sourceOrdered, ...other];
}

export function runsForPlanWeek<T extends WeeklyPlanRun>(
  runs: T[],
  lineId: string | null,
  weekStart: string | null,
  weekEnd: string | null,
) {
  if (!lineId || !weekStart || !weekEnd) return [];
  const start = Date.parse(`${weekStart}T00:00:00+03:00`);
  const end = Date.parse(`${weekEnd}T00:00:00+03:00`) + 86_400_000;
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
  return runs
    .filter((run) =>
      run.lineId === lineId &&
      run.planItemKind !== "ACTIVITY" &&
      run.disposition !== "CANCELLED" &&
      (planIntersects(run.plannedStartAt, run.plannedEndAt, start, end) ||
        (!run.plannedStartAt && run.shiftDate >= weekStart && run.shiftDate <= weekEnd)),
    )
    .sort(comparePlannedRuns);
}
